import React, { useCallback } from 'react';
import { Drawer } from 'antd';
import { A11Y_ROLES } from '@/utils/a11y-attrs';
import './index.scss';

export interface RoadTestDrawerShellProps {
  /** 抽屉是否展开 */
  open: boolean;
  /** 关闭抽屉回调 */
  onClose: () => void;
  /** 切换抽屉展开/收起回调（toggle 按钮调用） */
  onToggle?: () => void;
  /** 是否显示收起/展开按钮（抽屉展开时显示收起，关闭时显示展开），默认 true */
  showToggle?: boolean;
  /** 抽屉标题 */
  title?: React.ReactNode;
  /** 抽屉宽度 */
  width?: string | number;
  /** 是否显示抽屉标题右上角关闭按钮（默认显示） */
  closable?: boolean;
  /** 是否显示遮罩（默认 false，创建模式可传 true） */
  mask?: boolean;
  /** 关闭时是否销毁抽屉内容（性能优化场景可开启） */
  destroyOnClose?: boolean;
  /** 抽屉内容 */
  children: React.ReactNode;
}

/**
 * 通用路测抽屉壳：toggle 展开/收起 + Ant Design Drawer
 * 可被评价抽屉、整车回放等页面复用
 *
 * - showToggle: 控制抽屉收起/展开按钮。展开时显示「›路测」收起按钮，关闭时
 *   显示「‹路测」展开按钮。创建模式下可传 false，关闭后用户通过「路测评价」
 *   入口重新打开。
 */
const RoadTestDrawerShell: React.FC<RoadTestDrawerShellProps> = ({
  open,
  onClose,
  onToggle,
  showToggle = true,
  title,
  width = '21%',
  closable = true,
  mask = false,
  destroyOnClose = false,
  children,
}) => {
  /**
   * 点击收起/展开按钮的回调
   * 1. 如果传了 onToggle，则调用 onToggle
   * 2. 如果没传 onToggle，则调用 onClose（收起）
   */
  const handleToggle = useCallback(() => {
    if (onToggle) {
      onToggle();
    } else if (open) {
      onClose();
    }
  }, [open, onClose, onToggle]);

  return (
    <>
      {/* 收起按钮：抽屉展开时显示 */}
      {open && showToggle && (
        <div
          {...A11Y_ROLES.button('收起路测面板')}
          className="road-test-drawer-toggle drawer-open"
          onClick={handleToggle}
          data-testid="road-test-drawer-toggle"
        >
          <span className="toggle-icon">›</span>
          <span className="toggle-text">路测</span>
        </div>
      )}
      {/* 展开按钮：抽屉关闭时显示 */}
      {!open && showToggle && (
        <div
          {...A11Y_ROLES.button('展开路测面板')}
          className="road-test-drawer-toggle"
          onClick={handleToggle}
          data-testid="road-test-drawer-toggle"
        >
          <span className="toggle-icon">‹</span>
          <span className="toggle-text">路测</span>
        </div>
      )}
      <Drawer
        open={open}
        onClose={onClose}
        width={width}
        mask={mask}
        className="road-test-drawer"
        data-testid="road-test-drawer"
        title={title}
        closable={closable}
        destroyOnClose={destroyOnClose}
        zIndex={1000}
      >
        {children}
      </Drawer>
    </>
  );
};

export default React.memo(RoadTestDrawerShell);
